import Head from 'next/head';
import { gql } from '@apollo/client';

/**
 * Provide SEO related meta tags to a page.
 *
 * @param {Props} props The props object.
 * @param {string} props.title Used for the page title, og:title, twitter:title, etc.
 * @param {string} props.description Used for the meta description, og:description, twitter:description, etc.
 * @param {string} props.featuredImageUrl Used for the og:image and twitter:image. NOTE: Must be an absolute url.
 * @param {string} props.url Used for the og:url and twitter:url.
 *
 * @returns {React.ReactElement} The SEO component
 */
export function SEO({ siteTitle, siteDescription, featuredImageUrl, url, seo, pageTitle }) {
	const title = seo?.title || pageTitle || siteTitle;
	const description = seo?.description || siteDescription;
	const robotsMetaTag = Array.isArray(seo?.robotsMetaTag) ? seo.robotsMetaTag.join(', ') : null;

	if (!title && !description && !featuredImageUrl && !url) {
		return null;
	}


	return (
		<>
			<Head>
				<meta property="og:type" content="website" />
				<meta property="twitter:card" content="summary_large_image" />

				{title && (
					<>
						<title>{title}</title>
						<meta name="title" content={title} />
						<meta property="og:title" content={title} />
						<meta property="twitter:title" content={title} />
					</>
				)}

				{description && (
					<>
						<meta name="description" content={description} />
						<meta property="og:description" content={description} />
						<meta property="twitter:description" content={description} />
					</>
				)}

				{featuredImageUrl && (
					<>
						<meta property="og:image" content={featuredImageUrl} />
						<meta property="twitter:image" content={featuredImageUrl} />
					</>
				)}

				{url && (
					<>
						<meta property="og:url" content={url} />
						<meta property="twitter:url" content={url} />
					</>
				)}

				{robotsMetaTag && <meta name="robots" content={robotsMetaTag} />}
			</Head>
		</>
	);
}

SEO.fragments = {
	entry: gql`
		fragment PageSeo on Seo {
			description
			title
			robotsMetaTag
		}
	`,
};
